import * as THREE from "three";
import { MATS } from "../materials.js";
import * as B from "./backspec.js";

/**
 * The raised timber deck off the back of the house -- weathered grey boards
 * at kitchen level, a black-spindle balustrade on timber posts with solar
 * cap lights, and a dog-leg stair off the back-left corner: four risers down
 * to a small platform, a left turn, two more onto the flagstone patio.
 *
 * Built in the garden's own frame -- y = 0 is the lawn -- and placed one
 * terrace down by spec.MODULE_GRADE.  That is what makes "kitchen level"
 * literal: 1.35 m above this lawn is 0.55 m above the street, which is the
 * floor inside, so the patio door needs no step on either side of it.  Every
 * height below is measured from the lawn, not from the street.
 *
 * This is the garden's main climbing structure.  The cat jumps 0.95 m and
 * steps 0.23 m; the chain it gets is:
 *
 *   stone 0.05 -> tread 0.267 -> LANDING 0.483 (turn left)
 *              -> treads 0.700 / 0.917 / 1.133 -> deck 1.35
 *                                        (six risers of (1.35-0.05)/6 = 0.217:
 *                                         each is WALKED -- just inside the
 *                                         cat's 0.23 step.  Written as hops
 *                                         when the step-up was 0.13; the
 *                                         number moved, the treads did not.)
 *   deck 1.35  -> lot fence top 1.81 (+0.46, the boundary fence at x 6.75)
 *              -> rail top 2.32 (+0.51) -> post cap light 2.415 (+0.095 -- a
 *                                          walk-up from the rail, no jump)
 *
 * The rail stays at the photo's 2.32, which is 0.97 above the deck and so
 * over the 0.95 plain jump.  It used to carry a deliberate ladder as well --
 * a planted pot at 1.69 and a stack of empties at 1.80 by the +X rail, the
 * way the porch uses its chair -- and that dressing has been taken off.
 *
 * Losing it costs nothing, which is worth recording because it is not
 * obvious: the +X boundary fence runs the length of the deck a metre out from
 * the rail, and its top is 1.81, so the deck reaches the fence in 0.46 and
 * the fence reaches the rail in 0.51.  reach.mjs still calls the rail and
 * both cap lights reachable with the pots gone -- verified, not assumed.  The
 * charged pounce clears the 0.97 direct as well, but the fence route is the
 * one that does not depend on the player knowing to gather.
 *
 * That does tie this deck's climbing chain to fences.js.  Lower that boundary
 * fence and the rail becomes pounce-only.
 *
 * Under-deck is ENCLOSED: one solid collider fills the whole footprint to
 * grade, and a slatted timber skirt over a dark void panel closes it
 * visually, so there is no half-open crawl space to get stuck in.  The
 * under-stair and under-landing volumes are likewise solid (tread and
 * landing colliders run to grade, closed risers in front, stringer boards
 * at the flanks, skirted landing faces).
 */

const D = B.DECK;
const TOP = D.top;                      // 1.35
const RAIL = D.railTop;                 // 2.32
const MIDX = (D.minX + D.maxX) / 2;     // 3.075
const MIDZ = (D.minZ + D.maxZ) / 2;     // -13.475
const W = D.maxX - D.minX;              // 5.05
const DEPTH = D.maxZ - D.minZ;          // 3.95

// Balustrade lines, inset 0.11 from the platform edges.
const LX = D.minX + 0.11;               // 0.66 -- also the left stair rail
const RX = D.maxX - 0.11;               // 5.49
const FZ = D.minZ + 0.11;               // -15.34
const HZ = D.maxZ - 0.12;               // -11.62 -- posts at the house wall
const MIDL = -13.48;                    // mid posts on the long side runs

// ---------------------------------------------------------------------------
// Dog-leg stair, derived locally.  backspec's stepMinX/stepMaxX/steps still
// describe the old straight seven-riser flight and no longer fit this stair,
// so nothing below reads them: the client's stair leaves the deck at its
// back-LEFT corner (min-x, min-z -- your left is -X when you face away from
// the house), drops four risers in -Z to a platform, turns left, and drops
// two more in -X onto the flagstone.  Six risers carry the deck's 1.35 down
// to the stone at 0.05, so each is (1.35 - 0.05)/6 = 0.2167 -- six equal cat
// hops with a flat rest at the turn.
const STONE = 0.05;                     // flagstone top (patio.js: 0.046..0.062)
const RISE = (TOP - STONE) / 6;         // 0.21667
const GOING = 0.26;                     // ~40 degrees with the 0.217 riser
const FLIGHT_W = 1.15;                  // a one-person flight, not the old 1.9
const LAND = TOP - 4 * RISE;            // 0.48333 -- the turn platform's top
const LAND_SIZE = 1.15;                 // square, flush with both flights

const F1X = D.minX + FLIGHT_W / 2;      // 1.125 -- flight 1 centre (runs -Z)
const F1R = D.minX + FLIGHT_W;          // 1.70  -- flight 1 right edge
const LZ0 = D.minZ - 3 * GOING;         // -16.23 -- landing near (deck) edge
const LZ1 = LZ0 - LAND_SIZE;            // -17.38 -- landing far edge
const LZC = (LZ0 + LZ1) / 2;            // -16.805 -- also flight 2's centre

// Stair rail lines, inset 0.11 like the deck's.
const SRX = F1R - 0.11;                 // 1.59  -- flight-1 right / landing east
const NZ2 = LZ0 - 0.11;                 // -16.34 -- flight-2 north (house side)
const SZ2 = LZ1 + 0.11;                 // -17.27 -- landing south / flight-2 south
const GUARD = LAND + 0.97;              // 1.4533 -- landing guard, deck proportion
const FOOT_X = 0.10;                    // bottom newels, on the stone
const Z1_TOP = FZ - 0.06;               // flight-1 rails start under the deck posts

// Nosing lines as functions of the run coordinate, and rails 0.92 above them.
const THETA = Math.atan2(RISE, GOING);
const noseY = (z) => TOP - RISE + (z - D.minZ) * (RISE / GOING);     // flight 1
const noseX = (x) => LAND + (x - D.minX) * (RISE / GOING);           // flight 2
const railY = (z) => noseY(z) + 0.92;
const railYX = (x) => noseX(x) + 0.92;

export function build(b) {
  platform(b);
  understructure(b);
  balustrade(b);
  dogLegStair(b);

  // Climbing-route rungs, bottom to top: stone, tread, landing, three treads.
  b.mark("deck.tread1", D.minX - GOING / 2, LAND - RISE, LZC);
  b.mark("deck.landing", F1X, LAND, LZC);
  for (let k = 3; k >= 1; k--) {
    b.mark(`deck.tread${5 - k}`, F1X, TOP - k * RISE, D.minZ - (k - 0.5) * GOING);
  }
  b.mark("deck.top", MIDX, TOP, MIDZ);
  b.mark("deck.railTop", RX, RAIL, -13.00);
  b.mark("deck.capLight", RX, RAIL + 0.095, MIDL);

  // The stair now spills onto the patio at ~(0.10, -16.8); no route runs
  // through there today, but keep the check so a future nudge cannot creep
  // onto one.
  if (!B.clearOf(FOOT_X, LZC, 0.6)) console.warn("deck: stair foot blocks a route");
}

// ---------------------------------------------------------------------------
function platform(b) {
  // Dark underlay so the gaps between boards never show sky or void.
  b.block(MIDX, 1.295, MIDZ, W - 0.02, 0.015, DEPTH - 0.02,
          MATS.deckShade, { solid: false });

  // Boards run along X, 28 across the depth, every third one the shaded
  // batch so the boarding reads (the builder's per-piece tint does the rest).
  const n = 28;
  const pitch = DEPTH / n;
  for (let i = 0; i < n; i++) {
    const z = D.minZ + (i + 0.5) * pitch;
    b.block(MIDX, 1.31, z, W, 0.04, pitch - 0.008,
            i % 3 === 2 ? MATS.deckShade : MATS.deckGrey,
            { solid: false, tile: 0.8 });
  }

  // ONE collider for the platform, grade to board top: the deck is walkable
  // at exactly 1.35 and the space beneath is solid (the enclosed choice --
  // no low crawl volume for the cat to wedge into).
  b.collider(MIDX, 0, MIDZ, W, TOP, DEPTH);
}

function understructure(b) {
  // Rim joist band under the board edges; its front face doubles as the
  // top riser (tread 1.133 -> deck 1.35) above the stair opening.
  b.block(MIDX, 1.09, D.minZ + 0.05, W, 0.22, 0.10, MATS.deckShade, { solid: false });
  b.block(D.minX + 0.05, 1.09, MIDZ, 0.10, 0.22, DEPTH, MATS.deckShade, { solid: false });
  b.block(D.maxX - 0.05, 1.09, MIDZ, 0.10, 0.22, DEPTH, MATS.deckShade, { solid: false });

  // Support posts to grade, proud of the skirt so the slats read as infill
  // between them.  None on the stair span (x 0.55..1.70) -- the flight hides
  // that bay.
  const posts = [
    [1.82, -15.39], [2.62, -15.39], [4.05, -15.39], [5.49, -15.39],
    [0.61, -13.48], [0.61, -11.72],
    [5.49, -13.48], [5.49, -11.72],
  ];
  for (const [x, z] of posts) {
    b.block(x, 0, z, 0.10, 1.10, 0.10, MATS.timber, { solid: false });
  }

  // Skirt: vertical slats over a recessed dark panel on the three faces the
  // garden sees (the fourth is the house wall).  All visual -- the platform
  // collider already makes these faces solid.
  skirt(b, "x", 1.75, 5.40, -15.405, -15.35);          // front, right of stairs
  skirt(b, "z", -15.30, -11.56, 0.60, 0.665);          // -X side, facing patio
  skirt(b, "z", -15.30, -11.56, 5.55, 5.485);          // +X side, side passage
}

/** Slat-over-panel infill; y0/h default to the full deck skirt height. */
function skirt(b, axis, a0, a1, atSlat, atPanel, y0 = 0.02, h = 1.08) {
  const mid = (a0 + a1) / 2, len = a1 - a0;
  if (axis === "x") {
    b.block(mid, y0 + 0.01, atPanel, len, h - 0.01, 0.025, MATS.metalDark, { solid: false });
  } else {
    b.block(atPanel, y0 + 0.01, mid, 0.025, h - 0.01, len, MATS.metalDark, { solid: false });
  }
  const n = Math.max(1, Math.floor(len / 0.16));
  for (let i = 0; i < n; i++) {
    const p = a0 + (i + 0.5) * (len / n);
    const m = i % 4 === 3 ? MATS.deckShade : MATS.deckGrey;
    if (axis === "x") b.block(p, y0, atSlat, 0.055, h, 0.03, m, { solid: false });
    else b.block(atSlat, y0, p, 0.03, h, 0.055, m, { solid: false });
  }
}

// ---------------------------------------------------------------------------
function balustrade(b) {
  // Timber posts, each wearing a black solar cap light.  The LX and SRX
  // front posts double as the stair-head newels either side of the opening.
  const posts = [
    [LX, HZ], [LX, MIDL], [LX, FZ],
    [SRX, FZ], [2.89, FZ], [4.19, FZ], [RX, FZ],
    [RX, MIDL], [RX, HZ],
  ];
  for (const [x, z] of posts) postCapLight(b, x, z);

  // Rails and spindles between the posts.  The step opening is x 0.66..1.59
  // on the front edge; the side runs continue to the house wall past HZ.
  const F = 0.06;
  railRun(b, "z", FZ + F, MIDL - F, LX);
  railRun(b, "z", MIDL + F, -11.53, LX);
  railRun(b, "x", SRX + F, 2.89 - F, FZ);
  railRun(b, "x", 2.89 + F, 4.19 - F, FZ);
  railRun(b, "x", 4.19 + F, RX - F, FZ);
  railRun(b, "z", FZ + F, MIDL - F, RX);
  railRun(b, "z", MIDL + F, -11.53, RX);

  // ONE collider per edge (not per segment, never per spindle): a clean beam
  // topping out at exactly RAIL, so the cat balance-walks the rail without
  // snagging on anything invisible.
  b.collider(LX, TOP, -13.445, 0.14, RAIL - TOP, 3.89);
  b.collider(RX, TOP, -13.445, 0.14, RAIL - TOP, 3.89);
  b.collider((SRX + RX) / 2, TOP, FZ, RX - SRX + 0.12, RAIL - TOP, 0.14);
}

/**
 * One balustrade panel: timber top and bottom rails, black spindles between.
 * base/top default to the deck platform and its 2.32 rail; the landing
 * passes its own pair so the guard keeps the deck's proportions at 0.483.
 */
function railRun(b, axis, a0, a1, at, base = TOP, top = RAIL) {
  const len = a1 - a0;
  if (len < 0.1) return;
  const mid = (a0 + a1) / 2;
  const box = (p, y, w, h, t, m) => axis === "x"
    ? b.block(p, y, at, w, h, t, m, { solid: false })
    : b.block(at, y, p, t, h, w, m, { solid: false });

  box(mid, top - 0.055, len, 0.055, 0.10, MATS.deckGrey);    // top rail
  box(mid, base + 0.08, len, 0.05, 0.07, MATS.deckGrey);     // bottom rail
  const n = Math.max(1, Math.round(len / 0.12) - 1);
  for (let i = 1; i <= n; i++) {
    box(a0 + (len * i) / (n + 1), base + 0.13, 0.028,
        top - 0.055 - (base + 0.13), 0.028, MATS.metalDark);
  }
}

function postCapLight(b, x, z) {
  b.block(x, TOP, z, 0.09, 1.00, 0.09, MATS.deckGrey, { solid: false });
  b.block(x, 2.35, z, 0.13, 0.04, 0.13, MATS.metalDark, { solid: false });   // cap
  b.block(x, 2.39, z, 0.085, 0.025, 0.085, MATS.metalPale, { solid: false }); // lens
  // Cap collider sits on the rail-run collider: top 2.415, a 0.095 step up
  // from the rail top -- the cat can WALK from the rail onto a cap light.
  b.collider(x, RAIL, z, 0.14, 0.095, 0.14);
}

// ---------------------------------------------------------------------------
function dogLegStair(b) {
  flight1(b);
  landing(b);
  flight2(b);

  // Balustrade down both flights and around the landing, as the photo reads:
  // sloped rails on the flights, level guard on the platform's two closed
  // sides, capped newels at every corner of the turn and at the foot.
  for (const x of [LX, SRX]) flight1Rail(b, x);
  landingGuard(b);
  for (const z of [NZ2, SZ2]) flight2Rail(b, z);

  for (const [x, z, cap] of [
    [LX, NZ2, GUARD + 0.15],     // the turn: flight-1 left becomes flight-2 north
    [SRX, NZ2, GUARD + 0.15],    // pitch change: flight-1 right -> landing east
    [SRX, SZ2, GUARD + 0.15],    // landing SE corner
    [LX, SZ2, GUARD + 0.15],     // landing SW corner -> flight-2 south
    [FOOT_X, NZ2, railYX(FOOT_X) + 0.15],   // feet, on the stone (caps 1.18)
    [FOOT_X, SZ2, railYX(FOOT_X) + 0.15],
  ]) stairNewel(b, x, z, cap);
}

/** Three projecting treads down -Z; the deck's rim joist is the top riser. */
function flight1(b) {
  // Tread k (from the top) tops out at TOP - k*RISE: 1.133 / 0.917 / 0.700,
  // then the fourth riser lands on the platform at 0.483.
  for (let k = 1; k <= 3; k++) {
    const top = TOP - k * RISE;
    const zc = D.minZ - (k - 0.5) * GOING;
    b.block(F1X, top - 0.045, zc - 0.015, FLIGHT_W, 0.045, GOING + 0.03,
            k % 2 ? MATS.deckGrey : MATS.deckShade, { solid: false, tile: 0.8 });
    // Closed riser tucked behind the nosing lip.
    b.block(F1X, TOP - (k + 1) * RISE, D.minZ - k * GOING + 0.035,
            FLIGHT_W - 0.06, top - 0.045 - (TOP - (k + 1) * RISE), 0.03,
            MATS.deckShade, { solid: false });
    // Individual tread collider, solid to grade: the top is the exact tread
    // height and the wedge under the flight stays properly closed.
    b.collider(F1X, 0, zc, FLIGHT_W, top, GOING);
  }
  // Stringer boards close the flight's flanks under the treads; the flight
  // ends on the landing, so unlike a flight to grade they never near the toe.
  for (const x of [D.minX + 0.03, F1R - 0.03]) {
    slopedBoard(b, x, LZ0 + 0.02, D.minZ - 0.01, (z) => noseY(z) + 0.02,
                0.05, 0.24, MATS.deckShade);
  }
}

/** The turn platform: boarded like the deck, skirted, solid to grade. */
function landing(b) {
  // Dark underlay, then boards topping at exactly LAND, run along X like the
  // deck's; the field is shifted 0.015 toward -X so the down-stair (west)
  // edge gets a proper 3 cm nosing over flight 2's top riser.
  b.block(F1X, LAND - 0.055, LZC, LAND_SIZE - 0.02, 0.015, LAND_SIZE - 0.02,
          MATS.deckShade, { solid: false });
  const n = 8;
  const pitch = LAND_SIZE / n;
  for (let i = 0; i < n; i++) {
    b.block(F1X - 0.015, LAND - 0.04, LZ0 - (i + 0.5) * pitch,
            LAND_SIZE + 0.03, 0.04, pitch - 0.008,
            i % 3 === 2 ? MATS.deckShade : MATS.deckGrey,
            { solid: false, tile: 0.8 });
  }

  // Short skirts close the two faces the patio sees (east and south); the
  // north face is under flight 1 and the west face is flight 2's top riser.
  skirt(b, "z", LZ1 + 0.03, LZ0 - 0.03, F1R - 0.04, F1R - 0.075, 0.02, LAND - 0.06);
  skirt(b, "x", D.minX + 0.03, F1R - 0.03, LZ1 + 0.045, LZ1 + 0.08, 0.02, LAND - 0.06);

  // ONE collider, grade to board top -- walkable at exactly 0.483, and the
  // under-landing volume is solid, same enclosed choice as the deck itself.
  b.collider(F1X, 0, LZC, LAND_SIZE, LAND, LAND_SIZE);
}

/** One projecting tread down -X; the second riser lands on the flagstone. */
function flight2(b) {
  const top = LAND - RISE;                       // 0.267
  const xc = D.minX - GOING / 2;                 // 0.42
  b.block(xc - 0.015, top - 0.045, LZC, GOING + 0.03, 0.045, FLIGHT_W,
          MATS.deckGrey, { solid: false, tile: 0.8 });
  // Closed risers: under the landing's nosing (top 0.483 -> tread), and
  // under the tread's (tread -> stone; it starts at 0.02, just inside the
  // flagstone bed, so no sliver of void shows at the toe).
  b.block(D.minX + 0.035, top, LZC, 0.03, LAND - 0.045 - top, FLIGHT_W - 0.06,
          MATS.deckShade, { solid: false });
  b.block(D.minX - GOING + 0.035, 0.02, LZC, 0.03, top - 0.045 - 0.02,
          FLIGHT_W - 0.06, MATS.deckShade, { solid: false });
  // Tread collider to grade -- the exact 0.267 hop, nothing hollow beneath.
  b.collider(xc, 0, LZC, GOING, top, FLIGHT_W);

  // Stringers; 0.24 deep reaches below the stone bed at the toe.
  for (const z of [LZ0 - 0.03, LZ1 + 0.03]) {
    slopedBoardX(b, z, D.minX - GOING + 0.01, D.minX - 0.01,
                 (x) => noseX(x) + 0.02, 0.05, 0.24, MATS.deckShade);
  }
}

/** A box laid parallel to the stair pitch; yTop gives its top surface at z. */
function slopedBoard(b, x, z0, z1, yTop, w, t, mat) {
  const g = new THREE.BoxGeometry(w, t, (z1 - z0) / Math.cos(THETA));
  g.rotateX(-THETA);                    // +z end (toward the house) rises
  g.translate(x, (yTop(z0) + yTop(z1)) / 2 - t / 2, (z0 + z1) / 2);
  b.add(g, mat, { tile: 0.8 });
}

/** As slopedBoard, but pitched along x; the +x end (toward the deck) rises. */
function slopedBoardX(b, z, x0, x1, yTop, w, t, mat) {
  const g = new THREE.BoxGeometry((x1 - x0) / Math.cos(THETA), t, w);
  g.rotateZ(THETA);
  g.translate((x0 + x1) / 2, (yTop(x0) + yTop(x1)) / 2 - t / 2, z);
  b.add(g, mat, { tile: 0.8 });
}

/**
 * Flight 1's balustrade, one side: sloped timber rails from under the deck
 * posts down to the turn newels, black spindles between.  ONE collider for
 * the run, grade-to-rail, so the flank is a solid wall -- the cat cannot
 * fall off the flight sideways and never snags on a spindle.  (The collider
 * top is level while the rail slopes, so there is a little invisible wall
 * above the rail's low end; a wall, not a snag, and it only guards harder.)
 */
function flight1Rail(b, x) {
  slopedBoard(b, x, NZ2, Z1_TOP, railY, 0.09, 0.055, MATS.deckGrey);
  slopedBoard(b, x, NZ2, Z1_TOP, (z) => railY(z) - 0.70, 0.07, 0.045,
              MATS.deckGrey);
  for (let z = NZ2 + 0.10; z < Z1_TOP - 0.04; z += 0.12) {
    b.block(x, railY(z) - 0.695, z, 0.028, 0.64, 0.028,
            MATS.metalDark, { solid: false });
  }
  b.collider(x, 0, (Z1_TOP + NZ2 - 0.11) / 2, 0.12,
             railY(Z1_TOP) + 0.01, Z1_TOP - NZ2 + 0.11);
}

/** Level guard around the landing's closed sides, at the deck's proportion. */
function landingGuard(b) {
  const F = 0.06;
  railRun(b, "z", SZ2 + F, NZ2 - F, SRX, LAND, GUARD);      // east side
  railRun(b, "x", LX + F, SRX - F, SZ2, LAND, GUARD);       // south side
  // One collider per run, newel to newel; they overlap the flight runs at
  // the corners so the guard has no invisible gap to slip through.
  b.collider(SRX, 0, (NZ2 + SZ2) / 2, 0.12, GUARD + 0.01, NZ2 - SZ2 + 0.09);
  b.collider((LX + SRX) / 2, 0, SZ2, SRX - LX + 0.11, GUARD + 0.01, 0.12);
}

/** Flight 2's balustrade, one side: same treatment pitched along -X. */
function flight2Rail(b, z) {
  slopedBoardX(b, z, FOOT_X, LX, railYX, 0.09, 0.055, MATS.deckGrey);
  slopedBoardX(b, z, FOOT_X, LX, (x) => railYX(x) - 0.70, 0.07, 0.045,
               MATS.deckGrey);
  for (let x = FOOT_X + 0.10; x < LX - 0.06; x += 0.12) {
    b.block(x, railYX(x) - 0.695, z, 0.028, 0.64, 0.028,
            MATS.metalDark, { solid: false });
  }
  b.collider((FOOT_X - 0.06 + LX + 0.06) / 2, 0, z, LX - FOOT_X + 0.12,
             railYX(LX) + 0.01, 0.12);
}

function stairNewel(b, x, z, capTop) {
  // No collider of its own: every newel sits entirely inside a run's box.
  b.block(x, 0, z, 0.09, capTop - 0.065, 0.09, MATS.deckGrey, { solid: false });
  b.block(x, capTop - 0.065, z, 0.13, 0.04, 0.13, MATS.metalDark, { solid: false });
  b.block(x, capTop - 0.025, z, 0.085, 0.025, 0.085, MATS.metalPale, { solid: false });
}

